import { useQuery } from '@tanstack/react-query';
import { get } from '@/lib/apiClient';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AnalyticsPeriod = '7d' | '30d' | '90d';

export interface AnalyticsMetricChanges {
  delivery_rate: number;
  open_rate: number;
  click_rate: number;
  reply_rate: number;
  bounce_rate: number;
  unsubscribed_count: number;
}

export interface AnalyticsMetrics {
  total_sent: number;
  delivery_rate: number;
  open_rate: number;
  click_rate: number;
  reply_rate: number;
  bounce_rate: number;
  unsubscribed_count: number;
  changes: AnalyticsMetricChanges;
}

export interface AnalyticsTimeSeriesPoint {
  date: string;
  sent: number;
  opened: number;
  clicked: number;
}

export interface AnalyticsTopCampaign {
  name: string;
  sent: number;
  delivered_pct: number;
  open_pct: number;
  click_pct: number;
  reply_pct: number;
}

export interface AnalyticsTopSequence {
  id: string;
  name: string;
  status: string;
  active_count: number;
  open_pct: number;
  reply_pct: number;
}

export interface AnalyticsTopSegment {
  id: string;
  name: string;
  open_pct: number;
  reply_pct: number;
}

export interface AnalyticsDeliveryHealth {
  delivered_pct: number;
  soft_bounce_pct: number;
  hard_bounce_pct: number;
}

export interface AnalyticsABTest {
  test_name: string;
  sequence_name: string;
  variant_a_rate: number;
  variant_b_rate: number;
  metric: string;
  winner: 'A' | 'B' | null;
  confidence: number;
}

export interface AnalyticsSequenceFunnel {
  enrolled: number;
  step1_sent: number;
  opened: number;
  not_replied: number;
  replied: number;
  converted: number;
}

export interface AnalyticsDashboardData {
  metrics: AnalyticsMetrics;
  time_series: AnalyticsTimeSeriesPoint[];
  top_campaigns: AnalyticsTopCampaign[];
  top_sequences: AnalyticsTopSequence[];
  top_segments: AnalyticsTopSegment[];
  delivery_health: AnalyticsDeliveryHealth;
  ab_tests: AnalyticsABTest[];
  sequence_funnel: AnalyticsSequenceFunnel;
}

export interface AnalyticsBestSendingTimesSlot {
  hour: number;
  label: string;
}

export interface AnalyticsBestSendingTimes {
  window_start: string;
  window_end: string;
  slots: AnalyticsBestSendingTimesSlot[];
  days: string[];
  grid: Record<string, Record<string, number | null>>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAnalytics(period: AnalyticsPeriod) {
  return useQuery({
    queryKey: ['analytics', 'dashboard', period],
    queryFn: () => get<AnalyticsDashboardData>(`analytics/dashboard?period=${period}`),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

export function useAnalyticsBestSendingTimes(period: AnalyticsPeriod) {
  return useQuery({
    queryKey: ['analytics', 'best-sending-times', period],
    queryFn: () => get<AnalyticsBestSendingTimes>(`analytics/best-sending-times?period=${period}`),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
